import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from 'semantic-ui-react';
import './styles.css';

function ImageCarousel({ images, autoplayInterval = 4000 }) {
  // Create infinite loop by duplicating images
  const extendedImages = useMemo(() => {
    const duplicateCount = 2; // Number of duplicates on each side
    const before = [...images.slice(-duplicateCount)];
    const after = [...images.slice(0, duplicateCount)];
    return [...before, ...images, ...after];
  }, [images]);

  const [currentIndex, setCurrentIndex] = useState(2); // Start at first real image
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => prevIndex + 1);
  }, [isTransitioning]);

  const goToPrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => prevIndex - 1);
  }, [isTransitioning]);

  // Handle infinite loop reset
  useEffect(() => {
    if (!isTransitioning) return;

    const timer = setTimeout(() => {
      setIsTransitioning(false);
      
      // Reset position if we're at a duplicate
      if (currentIndex >= extendedImages.length - 2) {
        // At or past the end duplicates, jump to beginning
        setCurrentIndex(2);
      } else if (currentIndex <= 1) {
        // At or before the start duplicates, jump to end
        setCurrentIndex(extendedImages.length - 3);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [currentIndex, isTransitioning, extendedImages.length]);

  // Autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      goToNext();
    }, autoplayInterval);

    return () => clearInterval(interval);
  }, [goToNext, autoplayInterval]);

  // Touch handlers for swipe (horizontal swipe)
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Calculate actual index for dots (wrapping around the original array)
  const getActualIndex = (index) => {
    const actualIndex = (index - 2) % images.length;
    return actualIndex < 0 ? actualIndex + images.length : actualIndex;
  };

  const actualCurrentIndex = getActualIndex(currentIndex);

  return (
    <div className="image-carousel-outer">
      <div className="image-carousel-container">
      {/* Images Container with peek effect */}
      <div 
        className="image-carousel-wrapper"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {extendedImages.map((image, index) => {
          const offset = index - currentIndex;
          const absOffset = Math.abs(offset);
          
          // Calculate position and scale
          let translateX = offset * 35; // Spacing between cards
          let scale = 1;
          let zIndex = extendedImages.length - absOffset;
          let opacity = 1;
          let rotateY = 0;
          
          if (offset === 0) {
            // Current image - centered and largest
            translateX = 0;
            scale = 1;
            zIndex = extendedImages.length + 1;
            opacity = 1;
          } else if (absOffset === 1) {
            // Adjacent images - subtle hints on sides
            translateX = offset * 42;
            scale = 0.65;
            opacity = 0.35;
            rotateY = offset * 6;
          } else if (absOffset === 2) {
            // Second level - nearly invisible
            translateX = offset * 48;
            scale = 0.52;
            opacity = 0.12;
            rotateY = offset * 10;
          } else {
            // Hidden images
            translateX = offset * 50;
            scale = 0.5;
            opacity = 0;
          }

          return (
            <div
              key={index}
              className={`carousel-image-card ${isTransitioning ? 'transitioning' : ''}`}
              style={{
                transition: isTransitioning ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                transform: `translateX(${translateX}%) scale(${scale}) rotateY(${rotateY}deg)`,
                opacity: opacity,
                zIndex: zIndex,
                pointerEvents: offset === 0 ? 'auto' : 'none'
              }}
            >
              <div
                className={`carousel-image-inner ${offset === 0 ? 'current' : 'adjacent'}`}
                style={{
                  backgroundImage: `url(${image.url})`
                }}
              >
                {/* Gradient overlay for better text readability */}
                <div className={`carousel-gradient-overlay ${offset === 0 ? 'current' : 'adjacent'}`} />
                
                {/* Info caption - always visible on current image */}
                {offset === 0 && (image.hoverText || image.caption) && (
                  <div className="carousel-info-caption">
                    {image.hoverText && (
                      <div className="carousel-info-title">
                        {image.hoverText}
                      </div>
                    )}
                    {image.hoverDate && (
                      <div className="carousel-info-date">
                        {image.hoverDate}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        disabled={isTransitioning}
        className="carousel-nav-button left"
      >
        <Icon name='chevron left' size='large' className="carousel-nav-icon" />
      </button>

      <button
        onClick={goToNext}
        disabled={isTransitioning}
        className="carousel-nav-button right"
      >
        <Icon name='chevron right' size='large' className="carousel-nav-icon" />
      </button>

      </div>

      {/* Dots + Counter bar */}
      <div className="carousel-controls-bar">
        <div className="carousel-dots">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index + 2); // +2 because of the duplicates at the start
                }
              }}
              className={`carousel-dot ${index === actualCurrentIndex ? 'active' : ''}`}
            />
          ))}
        </div>

        <div className="carousel-counter">
          {actualCurrentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
}

export default ImageCarousel;
